import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Warehouse, MapPin, Map as MapIcon, GitBranch, ShoppingCart, PackageOpen, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, ArrowLeftRight, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { StatCard, WidgetPanel, RoundedBarChart } from './dashboard/DashboardShared';
import {
  findWarehouse, findZone, branchesSourcingWarehouse,
  whPurchaseOrdersOf, stockTransfersOf, stockRowsOf, catalogProductsOf
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

export const WarehouseDashboard = ({ warehouseId, org, user, showNotification }) => {
  const { version: dataVersion } = useSharedStore();
  const warehouse = useMemo(() => findWarehouse(warehouseId), [warehouseId, dataVersion]);
  const zone = useMemo(() => (warehouse?.zoneId ? findZone(warehouse.zoneId) : null), [warehouse, dataVersion]);
  const orders = useMemo(() => whPurchaseOrdersOf(warehouseId), [warehouseId, dataVersion]);
  const sourcingBranches = useMemo(() => branchesSourcingWarehouse(warehouseId), [warehouseId, dataVersion]);
  const activeStock = useMemo(() => stockRowsOf(org?.id, warehouseId, 'active'), [org?.id, warehouseId, dataVersion]);
  const products = useMemo(() => catalogProductsOf(org?.id), [org?.id, dataVersion]);
  
  const { receivedTotal, dispatchedTotal, pendingIncoming, lowStockCount, recentActivity } = useMemo(() => {
    let received = 0;
    let pendingInc = 0;
    
    // Process Purchase Orders
    orders.forEach((po) => {
      const qty = Number(po.qty) || 0;
      if (po.status === 'Received') {
        received += qty;
      } else if (po.status === 'Sent' || po.status === 'Pending') {
        pendingInc += qty;
      }
    });

    // Process Dispatches
    let dispatched = 0;
    const transfers = stockTransfersOf(org?.id);
    transfers.forEach((t) => {
      if (t.fromLocationId === warehouseId && t.status !== 'cancelled') {
        dispatched += (t.lines || []).reduce((s, l) => s + (Number(l.qty) || 0), 0);
      }
    });
    
    // Low stock count
    let lowStock = 0;
    activeStock.forEach(s => {
      const minStock = Number(s.minStockThreshold) || 0;
      if (minStock > 0 && s.qty <= minStock) {
        lowStock++;
      }
    });

    // Build recent activity (mocking chronologically by just taking latest orders and transfers)
    const activity = [];
    orders.slice(-3).reverse().forEach(po => {
       activity.push({
         id: po.id,
         type: 'po',
         title: `Purchase Order ${po.status}`,
         desc: `${po.qty} units from Supplier`,
         icon: ShoppingCart,
         color: po.status === 'Received' ? '#10b981' : '#f59e0b',
         date: po.date || 'Recent'
       });
    });
    
    transfers.filter(t => t.fromLocationId === warehouseId).slice(-3).reverse().forEach(t => {
       activity.push({
         id: t.id,
         type: 'transfer',
         title: `Dispatch to Branch`,
         desc: `${t.lines?.length || 0} items transferred`,
         icon: ArrowUpFromLine,
         color: '#3b82f6',
         date: t.date || 'Recent'
       });
    });

    return { 
      receivedTotal: received, 
      dispatchedTotal: dispatched, 
      pendingIncoming: pendingInc,
      lowStockCount: lowStock,
      recentActivity: activity.slice(0, 4) // just top 4
    };
  }, [orders, org?.id, warehouseId, activeStock, dataVersion]);

  // Top products for the bar chart
  const topProductsChartData = useMemo(() => {
    const sorted = [...activeStock].sort((a, b) => b.qty - a.qty).slice(0, 5);
    return sorted.map(s => {
      const p = products.find(prod => prod.id === s.productId);
      return {
        label: p ? (p.name.substring(0, 10) + (p.name.length > 10 ? '...' : '')) : 'Unknown',
        value: Number(s.qty) || 0
      };
    });
  }, [activeStock, products]);

  if (!warehouse) {
    return (
      <div className="py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
        Warehouse not found.
      </div>
    );
  }

  const accent = org.theme.primary;
  const onHand = Math.max(0, receivedTotal - dispatchedTotal);

  return (
    <div className="flex flex-col gap-2 pb-6 origin-top" style={{ zoom: 0.85 }}>
      {/* Hero strip */}
      <section className="rounded-2xl px-6 py-6 flex flex-wrap items-center justify-between gap-4 overflow-hidden relative shadow-[0_4px_20px_rgb(0,0,0,0.08)]" style={{ background: `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 60%, #000 40%) 100%)`, color: '#fff' }}>
        <div className="space-y-2 max-w-2xl relative z-10">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] opacity-90 inline-flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-full backdrop-blur-md shadow-sm border border-white/10">
             <Warehouse size={10} /> My Warehouse
          </p>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight mt-1 text-white">
             {warehouse.name}
          </h2>
          <div className="flex items-center flex-wrap gap-3 mt-2 text-[11px] opacity-90 font-medium">
             {zone && <span className="inline-flex items-center gap-1.5"><MapIcon size={12} aria-hidden="true" /> {zone.name}</span>}
             {warehouse.address && <span className="inline-flex items-center gap-1.5"><MapPin size={12} aria-hidden="true" /> {warehouse.address}</span>}
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end gap-2 text-right relative z-10">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] opacity-70 text-white">Total Units On Hand</span>
          <span className="text-3xl font-bold tracking-tighter drop-shadow-sm text-white">{onHand}</span>
        </div>
        
        {/* Abstract background graphics */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-white opacity-5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-1/4 w-32 h-32 bg-black opacity-20 rounded-full blur-2xl translate-y-1/2"></div>
      </section>

      {/* KPI Grid */}
      <section className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
        <StatCard
          label="Pending Incoming"
          value={pendingIncoming}
          suffix="Units ordered"
          icon={ArrowDownToLine}
          iconColor="#3b82f6"
        />
        <StatCard
          label="Total Dispatched"
          value={dispatchedTotal}
          suffix="Units to branches"
          icon={ArrowUpFromLine}
          iconColor="#10b981"
        />
        <StatCard
          label="Branches Sourcing"
          value={sourcingBranches.length}
          suffix="Active links"
          icon={GitBranch}
          iconColor={accent}
        />
        <StatCard
          label="Stock Alerts"
          value={lowStockCount}
          suffix="Items low/out"
          icon={AlertTriangle}
          iconColor="#ef4444"
          trend={lowStockCount > 0 ? -100 : 100} // Red if > 0
        />
      </section>

      {/* Main Content Area */}
      <section className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        
        {/* Left Column: Top Products & Sourcing */}
        <div className="lg:col-span-2 flex flex-col gap-4 lg:gap-6">
          {/* Top Products Chart */}
          <WidgetPanel title="Highest Volume Stock" className="h-[280px]">
             {topProductsChartData.length > 0 ? (
               <RoundedBarChart data={topProductsChartData} color={accent} />
             ) : (
               <div className="flex-1 flex flex-col items-center justify-center text-slate-400 opacity-60">
                 <PackageOpen size={32} className="mb-2" />
                 <p className="text-xs font-semibold">No stock data available</p>
               </div>
             )}
          </WidgetPanel>
          
          {/* Linked Branches */}
          <WidgetPanel title="Branches Sourcing From Here" count={sourcingBranches.length}>
             <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 mt-2">
                <div className="flex flex-col gap-2">
                   {sourcingBranches.length > 0 ? sourcingBranches.map(b => (
                     <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50 hover:bg-slate-100/70 transition-colors">
                        <div className="flex items-center gap-3">
                           <div className="w-8 h-8 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-500 shadow-sm">
                              <GitBranch size={14} />
                           </div>
                           <div>
                              <p className="text-xs font-bold text-slate-800">{b.name}</p>
                              <p className="text-[10px] text-slate-500">{b.address || 'No address'}</p>
                           </div>
                        </div>
                        <span className="text-[10px] font-bold text-[var(--theme-primary)] bg-[var(--theme-primary-soft)] px-2 py-1 rounded uppercase">Active</span>
                     </div>
                   )) : (
                     <div className="text-center py-6 text-xs text-slate-400 font-medium">No branches linked yet.</div>
                   )}
                </div>
             </div>
          </WidgetPanel>
        </div>

        {/* Right Column: Recent Activity */}
        <div className="lg:col-span-1 flex flex-col h-full">
           <WidgetPanel title="Recent Activity" className="flex-1 min-h-[400px]">
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 mt-2">
                 {recentActivity.length > 0 ? (
                    <div className="relative border-l-2 border-slate-100 ml-4 pl-4 py-2 space-y-6">
                       {recentActivity.map((act, i) => (
                         <div key={i} className="relative">
                            <span 
                              className="absolute -left-[25px] top-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm z-10"
                              style={{ backgroundColor: act.color }}
                            >
                               <act.icon size={10} color="white" />
                            </span>
                            <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                               <p className="text-[10px] font-bold text-slate-400 mb-0.5">{act.date}</p>
                               <p className="text-xs font-bold text-slate-800">{act.title}</p>
                               <p className="text-[11px] text-slate-500 mt-0.5">{act.desc}</p>
                            </div>
                         </div>
                       ))}
                    </div>
                 ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60">
                       <Clock size={32} className="mb-2" />
                       <p className="text-xs font-semibold">No recent activity</p>
                    </div>
                 )}
              </div>
           </WidgetPanel>
        </div>
      </section>
    </div>
  );
};

WarehouseDashboard.propTypes = {
  warehouseId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
  user: PropTypes.object,
  showNotification: PropTypes.func,
};
